import { useTranslation } from 'react-i18next';
import { useAppDispatch, useSelector } from '../../../../store/hooks';
import ConfigurationTable from '../../components/ConfigurationTable/ConfigurationTable';
import { IConfigRow } from '../../components/ConfigurationTable/ConfigurationTable.utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadAllowedCrewMembers } from '../../../../store/features/allowedCrewMembers/allowedCrewMembersSlice';
import { deleteAllowedCrewMember } from '../../../../store/features/allowedCrewMembers/allowedCrewMembersSlice';
import { TableCellLayout, TableColumnDefinition, createTableColumn, MessageBar, TableColumnSizingOptions, ToolbarButton, Button, Tooltip } from '@fluentui/react-components';
import { Checkmark24Regular, Dismiss24Regular, Add24Regular, Delete24Filled, Delete24Regular, bundleIcon } from '@fluentui/react-icons';
import AddCrewDialog from './components/AddCrewDialog';
import ConfirmDeletionDialog from '../../components/ConfirmDeletionDialog/ConfirmDeletionDialog';
import { useConfirmDeletionDialog } from '../../components/ConfirmDeletionDialog/useConfirmDeletionDialog';

interface AllowedCrewMemberRow extends IConfigRow {
    timestamp?: string;
    isActive?: boolean;
}

const DeleteIconBundle = bundleIcon(Delete24Filled, Delete24Regular);

const AllowedCrewMembersPanel: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [addCrewOpen, setAddCrewOpen] = useState<boolean>(false);

    const {
        isOpen: deletionDialogOpen,
        itemId: deletionItemId,
        message: deletionDialogMessage,
        openDialog: openDeletionDialog,
        closeDialog: closeDeletionDialog,
    } = useConfirmDeletionDialog(
        useCallback(
            (staffNumber: string) =>
                t('AllowedCrewMember_DeletionDialog_Message').replace('{staffNumber}', staffNumber),
            [t]
        )
    );

    useEffect(() => {
        dispatch(loadAllowedCrewMembers());
    }, [dispatch]);

    const { items, loadStatus } = useSelector((x) => x.allowedCrewMembers);
    const { searchQuery } = useSelector((x) => x.appConfiguration);

    const configItems = useMemo(() => {
        const rows: AllowedCrewMemberRow[] = [];

        for (const item of items) {
            rows.push({
                id: item.rowKey,
                value: item.rowKey,
                timestamp: item.timestamp,
                isActive: item.isActive,
            });
        }

        return rows;
    }, [items]);

    const filteredItems = useMemo(() => {
        if (!searchQuery || typeof searchQuery !== 'string') {
            return configItems;
        }
        return configItems.filter((x) =>
            x.value && x.value.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [configItems, searchQuery]);

    const handleAddCrewOpen = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setAddCrewOpen(true);
    }, []);

    const handleAddCrewClose = useCallback(() => {
        setAddCrewOpen(false);
    }, []);

    const handleDeleteCrewMemberClick = useCallback((staffNumber: string) => {
        openDeletionDialog({ itemId: staffNumber, itemName: staffNumber });
    }, [openDeletionDialog]);

    const handleConfirmDeleteCrewMember = useCallback(() => {
        if (!deletionItemId) {
            return;
        }

        dispatch(deleteAllowedCrewMember(deletionItemId)).then(() => {
            dispatch(loadAllowedCrewMembers());
            closeDeletionDialog();
        });
    }, [closeDeletionDialog, deletionItemId, dispatch]);

    const allowedCrewColumnSizing: TableColumnSizingOptions = {
        staffNumber: {
            minWidth: 150,
            idealWidth: 250,
        },
        timestamp: {
            minWidth: 200,
            idealWidth: 350,
        },
        isActive: {
            minWidth: 100,
            idealWidth: 100,
        },
        actions: {
            minWidth: 100,
            idealWidth: 100,
        },
    };

    const columns = useMemo<TableColumnDefinition<AllowedCrewMemberRow>[]>(() => [
        createTableColumn<AllowedCrewMemberRow>({
            columnId: 'staffNumber',
            compare: (a, b) =>
                a.value.localeCompare(b.value, undefined, {
                    sensitivity: 'base',
                }),
            renderHeaderCell: () => t('TableHeader_StaffNumber'),
            renderCell: (item) => item.value || '-',
        }),
        createTableColumn<AllowedCrewMemberRow>({
            columnId: 'timestamp',
            compare: (a, b) => {
                const timeA = new Date(a.timestamp || '').getTime();
                const timeB = new Date(b.timestamp || '').getTime();
                return timeA - timeB;
            },
            renderHeaderCell: () => t('TableHeader_LastSynced'),
            renderCell: (item) => item.timestamp ? new Date(item.timestamp).toLocaleString() : '-',
        }),
        createTableColumn<AllowedCrewMemberRow>({
            columnId: 'isActive',
            compare: (a, b) =>
                (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0),
            renderHeaderCell: () => t('TableHeader_IsActive'),
            renderCell: (item) => {
                if (item.isActive === true) {
                    return <Checkmark24Regular />;
                } else if (item.isActive === false) {
                    return <Dismiss24Regular />;
                } else {
                    return '-';
                }
            },
        }),
        createTableColumn<AllowedCrewMemberRow>({
            columnId: 'actions',
            renderHeaderCell: () => <></>,
            renderCell: (item) => (
                <TableCellLayout>
                    <Tooltip content={t('Tooltip_Delete')} relationship="label">
                        <Button
                            appearance="transparent"
                            size="small"
                            icon={<DeleteIconBundle />}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteCrewMemberClick(item.id);
                            }}
                        />
                    </Tooltip>
                </TableCellLayout>
            ),
        }),
    ], [t, handleDeleteCrewMemberClick]);

    return (
        <>
            <MessageBar
                intent="info"
            >
                {t('AppConfiguration_AllowedCrewMembers_MsgBar24Hours')}
            </MessageBar>
            <ConfigurationTable
                items={filteredItems}
                columns={columns}
                loadStatus={loadStatus}
                columnSizingOptions={allowedCrewColumnSizing}
                bottomActions={
                    <ToolbarButton
                        onClick={handleAddCrewOpen}
                        icon={<Add24Regular />}
                    >
                        {t('ToolbarButton_AddCrew')}
                    </ToolbarButton>
                }
            />

            <AddCrewDialog
                open={addCrewOpen}
                onClose={handleAddCrewClose}
            />

            <ConfirmDeletionDialog
                open={deletionDialogOpen}
                message={deletionDialogMessage}
                onConfirm={handleConfirmDeleteCrewMember}
                onClose={closeDeletionDialog}
            />
        </>
    );
};

export default AllowedCrewMembersPanel;

